import { AreaChart, StyledCheckbox } from '@clickcease/cc-client-components-lib'
import { CheckboxIndicator } from '@radix-ui/react-checkbox'
import Highcharts from 'highcharts'
import highchartsMap from 'highcharts/modules/map'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { CheckIcon } from 'Assets/icons/CheckIcon'
import { DatedSummaryEntry, NormalizedSummaryData } from '../../../../../hooks/api/BMDataTypes'

import { Chart1SeriesId, Chart2SeriesId, SeriesConfig } from './InsightsWidget.models'
import {
  InsightsWidgetWrapper,
  InsightsWidgetChartWrapper,
  InsightsWidgetChartTitleSection,
  InsightsWidgetChartTitle,
  SeriesToggleContainer,
  SeriesToggleItem,
} from './InsightsWidget.styled'

interface InsightsWidgetProps {
  isPdf?: boolean
  summary: NormalizedSummaryData
}

if (typeof Highcharts === 'object') {
  highchartsMap(Highcharts)
}

const generateTotalVisitsChartOptions = (
  data: DatedSummaryEntry[],
  t: (key: string) => string,
  visibleSeries: Record<Chart1SeriesId, boolean>
): Highcharts.Options => {
  const processedData = data
    .map((entry) => {
      const date = new Date(entry.date)
      return {
        date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        protectedValidVisits: entry.protectedValidVisits,
        totalBlocked: entry.totalBlocked,
        whiteListed: entry.whiteListed,
        totalVisits: entry.protectedValidVisits + entry.totalBlocked + entry.whiteListed,
        invalidRate: (
          (entry.totalBlocked /
            (entry.protectedValidVisits + entry.totalBlocked + entry.whiteListed)) *
          100
        ).toFixed(2),
      }
    })
    .reverse()

  const categories = processedData.map((entry) => entry.date)

  const seriesConfig: Array<Highcharts.SeriesOptionsType> = [
    {
      id: 'validVisits',
      name: t('insightsWidget.chart1.validVisits'),
      data: processedData.map((entry) => entry.protectedValidVisits),
      color: '#E92165',
      type: 'area',
      visible: visibleSeries.validVisits,
      fillColor: {
        linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
        stops: [
          [0, 'rgba(149, 117, 205, 0.7)'],
          [1, 'rgba(149, 117, 205, 0.1)'],
        ],
      },
    },
    {
      id: 'totalBlocked',
      name: t('insightsWidget.chart1.totalBlocked'),
      data: processedData.map((entry) => entry.totalBlocked),
      color: '#F15A8C',
      type: 'area',
      visible: visibleSeries.totalBlocked,
      fillColor: {
        linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
        stops: [
          [0, 'rgba(149, 117, 205, 0.7)'],
          [1, 'rgba(149, 117, 205, 0.1)'],
        ],
      },
    },
    {
      id: 'knownCrawlers',
      name: t('insightsWidget.chart1.knownCrawlers'),
      data: processedData.map((entry) => entry.whiteListed),
      color: '#F591B2',
      type: 'area',
      visible: visibleSeries.knownCrawlers,
      fillColor: {
        linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
        stops: [
          [0, 'rgba(149, 117, 205, 0.7)'],
          [1, 'rgba(149, 117, 205, 0.1)'],
        ],
      },
    },
    {
      id: 'invalidRate',
      name: t('insightsWidget.chart1.invalidRate'),
      data: processedData.map((entry) => parseFloat(entry.invalidRate)),
      type: 'line',
      dashStyle: 'Dash',
      color: '#FFB3C6',
      yAxis: 1,
      tooltip: {
        valueSuffix: '%',
      },
      visible: visibleSeries.invalidRate,
    },
    {
      id: 'totalVisits',
      name: t('insightsWidget.chart1.totalVisits'),
      data: processedData.map((entry) => entry.totalVisits),
      color: '#FF6F91',
      type: 'line',
      tooltip: {
        valueSuffix: '',
      },
      visible: visibleSeries.totalVisits,
    },
  ]

  return {
    chart: {
      type: 'area',
      height: 220,
    },
    xAxis: {
      categories,
      title: {
        text: null,
      },
    },
    yAxis: [
      {
        title: {
          text: null,
        },
      },
      {
        title: {
          text: null,
        },
        opposite: true,
        labels: {
          format: '{value}%',
        },
      },
    ],
    tooltip: {
      shared: true,
    },
    plotOptions: {
      area: {
        stacking: 'normal',
        marker: {
          enabled: false,
        },
      },
    },
    series: seriesConfig,
  }
}

const generateInvalidTrafficChartOptions = (
  data: DatedSummaryEntry[],
  t: (key: string) => string,
  visibleSeries: Record<Chart2SeriesId, boolean>
): Highcharts.Options => {
  const processedData = data
    .map((entry) => {
      const date = new Date(entry.date)
      return {
        date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        blockedPageloads: entry.blockedPageloads,
        blockedSiteInteractions: entry.blockedSiteInteractions,
      }
    })
    .reverse()

  const categories = processedData.map((entry) => entry.date)

  const seriesConfig: Array<Highcharts.SeriesOptionsType> = [
    {
      id: 'invalidPageLoads',
      name: t('insightsWidget.chart2.invalidPageLoads'),
      data: processedData.map((entry) => entry.blockedPageloads),
      color: '#E92165',
      type: 'area',
      dashStyle: 'Dash',
      visible: visibleSeries.invalidPageLoads,
      fillColor: {
        linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
        stops: [
          [0, 'rgba(149, 117, 205, 0.7)'],
          [1, 'rgba(149, 117, 205, 0.1)'],
        ],
      },
    },
    {
      id: 'invalidSiteInteractions',
      name: t('insightsWidget.chart2.invalidSiteInteractions'),
      data: processedData.map((entry) => entry.blockedSiteInteractions),
      color: '#FF6F91',
      type: 'area',
      visible: visibleSeries.invalidSiteInteractions,
      fillColor: {
        linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
        stops: [
          [0, 'rgba(149, 117, 205, 0.7)'],
          [1, 'rgba(149, 117, 205, 0.1)'],
        ],
      },
    },
  ]

  return {
    chart: {
      type: 'area',
      height: 220,
    },
    xAxis: {
      categories,
      title: {
        text: null,
      },
    },
    yAxis: {
      title: {
        text: null,
      },
    },
    tooltip: {
      shared: true,
    },
    plotOptions: {
      area: {
        stacking: 'normal',
        marker: {
          enabled: false,
        },
      },
      series: {
        pointPlacement: 'on',
      },
    },
    series: seriesConfig,
  }
}

export const InsightsWidget: React.FC<InsightsWidgetProps> = ({ isPdf, summary }) => {
  const { t } = useTranslation('botMitigation')

  const [visibleChart1Series, setVisibleChart1Series] = useState<Record<Chart1SeriesId, boolean>>({
    validVisits: true,
    totalBlocked: true,
    knownCrawlers: true,
    invalidRate: true,
    totalVisits: true,
  })

  const [visibleChart2Series, setVisibleChart2Series] = useState<Record<Chart2SeriesId, boolean>>({
    invalidPageLoads: true,
    invalidSiteInteractions: true,
  })

  const handleChart1SeriesToggle = (seriesId: Chart1SeriesId) => {
    setVisibleChart1Series((prev) => ({
      ...prev,
      [seriesId]: !prev[seriesId],
    }))
  }

  const handleChart2SeriesToggle = (seriesId: Chart2SeriesId) => {
    setVisibleChart2Series((prev) => ({
      ...prev,
      [seriesId]: !prev[seriesId],
    }))
  }

  const datedSummaries = useMemo<DatedSummaryEntry[]>(() => {
    return summary.rawData?.datedSummaries ?? []
  }, [summary.rawData])

  const totalVisitsChartOptions = useMemo(() => {
    if (!datedSummaries.length) return null
    return generateTotalVisitsChartOptions(datedSummaries, t, visibleChart1Series)
  }, [datedSummaries, t, visibleChart1Series])

  const invalidTrafficChartOptions = useMemo(() => {
    if (!datedSummaries.length) return null
    return generateInvalidTrafficChartOptions(datedSummaries, t, visibleChart2Series)
  }, [datedSummaries, t, visibleChart2Series])

  const chart1SeriesConfig: SeriesConfig<Chart1SeriesId>[] = [
    { id: 'validVisits', name: t('insightsWidget.chart1.validVisits'), color: '#E92165' },
    { id: 'totalBlocked', name: t('insightsWidget.chart1.totalBlocked'), color: '#F15A8C' },
    { id: 'knownCrawlers', name: t('insightsWidget.chart1.knownCrawlers'), color: '#F591B2' },
    { id: 'invalidRate', name: t('insightsWidget.chart1.invalidRate'), color: '#FFB3C6' },
    { id: 'totalVisits', name: t('insightsWidget.chart1.totalVisits'), color: '#FF6F91' },
  ]

  const chart2SeriesConfig: SeriesConfig<Chart2SeriesId>[] = [
    { id: 'invalidPageLoads', name: t('insightsWidget.chart2.invalidPageLoads'), color: '#E92165' },
    {
      id: 'invalidSiteInteractions',
      name: t('insightsWidget.chart2.invalidSiteInteractions'),
      color: '#FF6F91',
    },
  ]

  return (
    <InsightsWidgetWrapper>
      <InsightsWidgetChartWrapper>
        <InsightsWidgetChartTitleSection>
          <InsightsWidgetChartTitle>{t('insightsWidget.chart1.title')}</InsightsWidgetChartTitle>
          {!isPdf && (
            <SeriesToggleContainer chart1>
              {chart1SeriesConfig.map((series) => (
                <SeriesToggleItem key={series.id}>
                  <StyledCheckbox
                    id={`toggle-${series.id}`}
                    checked={visibleChart1Series[series.id]}
                    onCheckedChange={() => handleChart1SeriesToggle(series.id)}
                    style={{
                      backgroundColor: visibleChart1Series[series.id]
                        ? series.color
                        : 'transparent',
                      borderColor: series.color,
                      border: `1px solid ${series.color}`,
                      display: 'flex',
                      alignItems: 'baseline',
                    }}
                  >
                    <CheckboxIndicator>
                      <CheckIcon />
                    </CheckboxIndicator>
                  </StyledCheckbox>
                  {series.name}
                </SeriesToggleItem>
              ))}
            </SeriesToggleContainer>
          )}
        </InsightsWidgetChartTitleSection>

        {totalVisitsChartOptions && <AreaChart options={totalVisitsChartOptions} />}
      </InsightsWidgetChartWrapper>

      <InsightsWidgetChartWrapper>
        <InsightsWidgetChartTitleSection>
          <InsightsWidgetChartTitle>{t('insightsWidget.chart2.title')}</InsightsWidgetChartTitle>
          {!isPdf && (
            <SeriesToggleContainer>
              {chart2SeriesConfig.map((series) => (
                <SeriesToggleItem key={series.id}>
                  <StyledCheckbox
                    id={`toggle-${series.id}`}
                    checked={visibleChart2Series[series.id]}
                    onCheckedChange={() => handleChart2SeriesToggle(series.id)}
                    style={{
                      backgroundColor: visibleChart2Series[series.id]
                        ? series.color
                        : 'transparent',
                      borderColor: series.color,
                      border: `1px solid ${series.color}`,
                      display: 'flex',
                      alignItems: 'baseline',
                    }}
                  >
                    <CheckboxIndicator>
                      <CheckIcon />
                    </CheckboxIndicator>
                  </StyledCheckbox>
                  {series.name}
                </SeriesToggleItem>
              ))}
            </SeriesToggleContainer>
          )}
        </InsightsWidgetChartTitleSection>

        {invalidTrafficChartOptions && <AreaChart options={invalidTrafficChartOptions} />}
      </InsightsWidgetChartWrapper>
    </InsightsWidgetWrapper>
  )
}
